import React, { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Toast, Tooltip } from "@shopify/polaris"
import { TopMenu } from "../components/TopMenu.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider.jsx"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"
import { ScopesUpdateTutorial } from "../components/ScopesUpdateTutorial.jsx"

// Theme screenshots
import imgBeauty from "../assets/themes/beauty-and-cosmetics.png"
import imgFashion from "../assets/themes/fashion-and-accessories.png"
import imgHome from "../assets/themes/home-and-kitchen.png"
import imgPets from "../assets/themes/pets.png"
import imgSport from "../assets/themes/sport-and-fitness.png"
import imgTech from "../assets/themes/tech-and-gadgets.png"

const Themes = () => {
	const { state } = useContext(GlobalStateContext)
	const navigate = useNavigate()
	const { t } = useTranslation()
	const authenticatedFetch = useAuthenticatedFetch()
	const [installing, setInstalling] = useState("")
	const [publishAfterImport, setPublishAfterImport] = useState(false)

	// Permissions tutorial state
	const [showScopesTutorial, setShowScopesTutorial] = useState(false)
	const [requiredScopes, setRequiredScopes] = useState(["write_themes"]) // default
	const [hasNeccessaryScopes, setHasNeccessaryScopes] = useState(null)

	// Toast state
	const [showToast, setShowToast] = useState(false)
	const [toastContent, setToastContent] = useState("")
	const [toastIsError, setToastIsError] = useState(false)

	useEffect(() => {
		if (!state.hasValidKopyPlusSubscription) {
			navigate("/upgradeplus")
		}
	}, [state.kopyPlusSubscription])

	const themes = useMemo(
		() => [
			{
				id: "beauty-and-cosmetics",
				img: imgBeauty,
				title: t("themes.beauty_and_cosmetics"),
			},
			{
				id: "fashion-and-accessories",
				img: imgFashion,
				title: t("themes.fashion_and_accessories"),
			},
			{
				id: "home-and-kitchen",
				img: imgHome,
				title: t("themes.home_and_kitchen"),
			},
			{ id: "pets", img: imgPets, title: t("themes.pets") },
			{
				id: "sport-and-fitness",
				img: imgSport,
				title: t("themes.sport_and_fitness"),
			},
			{
				id: "tech-and-gadgets",
				img: imgTech,
				title: t("themes.tech_and_gadgets"),
			},
		],
		[t],
	)

	const handleImport = async (themeId) => {
		try {
			setInstalling(themeId)
			const res = await authenticatedFetch(`/api/themes/install`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					theme: themeId,
					publish: publishAfterImport,
				}),
			})

			if (!res?.ok) {
				let message = t("themes.install_error")
				try {
					const data = await res.json()
					if (data?.error === "missing_permissions") {
						message = t("themes.install_error_permissions")
						if (
							Array.isArray(data?.requiredScopes) &&
							data.requiredScopes.length
						) {
							setRequiredScopes(data.requiredScopes)
						} else {
							setRequiredScopes(["write_themes"])
						}
						setShowScopesTutorial(true)
						setHasNeccessaryScopes(false)
					} else if (data?.error) {
						message = `${message}\n${data.error}`
					}
				} catch (e) {
					const text = await res.text()
					if (text) message = `${message}\n${text}`
				}
				setToastContent(message)
				setToastIsError(true)
				setShowToast(true)
				return
			}

			const data = await res.json()
			setToastContent(
				publishAfterImport
					? t("themes.import_and_published_success")
					: t("themes.import_success"),
			)
			setToastIsError(false)
			setShowToast(true)
		} finally {
			setInstalling("")
		}
	}

	// If user is missing required permissions to install themes, show tutorial
	if (showScopesTutorial && hasNeccessaryScopes !== true) {
		return (
			<div>
				<TopMenu goBack="/" storeSelector />
				<div
					style={{
						paddingTop: "3.5rem",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<div style={{ maxWidth: "40rem", width: "100%" }}>
						<div style={{ marginTop: "3rem" }}>
							<ScopesUpdateTutorial
								requiredScopes={requiredScopes}
								hasNeccessaryScopes={hasNeccessaryScopes}
								onRequiredScopesChecked={() => {
									setHasNeccessaryScopes(true)
									setShowScopesTutorial(false)
								}}
								videoSrc="https://kopy.b-cdn.net/kopy_pages_scopes_tutorial.webm"
							/>
						</div>
					</div>
				</div>
				{showToast && (
					<Toast
						content={toastContent}
						error={toastIsError}
						onDismiss={() => setShowToast(false)}
						duration={10000}
					/>
				)}
			</div>
		)
	}

	return (
		<div>
			<TopMenu goBack="/" storeSelector />

			<div className="themes-container">
				<h1 className="themes-title">{t("themes.title")}</h1>
				<p className="themes-subtitle">{t("themes.subtitle")}</p>

				<div className="themes-publish">
					<label className="checkbox">
						<input
							type="checkbox"
							checked={publishAfterImport}
							onChange={(e) =>
								setPublishAfterImport(e.target.checked)
							}
						/>
						<span>{t("themes.publish_after_import")}</span>
					</label>
				</div>

				<div className="themes-grid">
					{themes.map((th) => (
						<div key={th.id} className="theme-card">
							<img
								src={th.img}
								alt={th.title}
								className="theme-img"
							/>
							<div className="theme-info">
								<div className="theme-name">{th.title}</div>
								{installing === th.id ? (
									<Tooltip
										content={
											<div
												style={{
													padding: "0.2rem 0.4rem",
												}}
											>
												<span
													style={{
														fontSize: "0.8rem",
														color: "#222",
													}}
												>
													{t(
														"themes.importing_tooltip",
													)}
												</span>
											</div>
										}
										preferredPosition="above"
									>
										<span
											style={{ display: "inline-block" }}
										>
											<button
												className="theme-import-button"
												disabled={!!installing}
												onClick={() =>
													handleImport(th.id)
												}
											>
												{t("themes.importing")}
											</button>
										</span>
									</Tooltip>
								) : (
									<button
										className="theme-import-button"
										disabled={!!installing}
										onClick={() => handleImport(th.id)}
									>
										{t("themes.import_theme")}
									</button>
								)}
							</div>
						</div>
					))}
				</div>

				<div className="themes-instructions">
					<p className="themes-instructions-title">
						{t("themes.how_to_install_title")}
					</p>
					<ol>
						<li>{t("themes.how_to_install_step_1")}</li>
						<li>{t("themes.how_to_install_step_2")}</li>
						<li>{t("themes.how_to_install_step_3")}</li>
					</ol>
				</div>
			</div>

			<style>{`
				.themes-container { 
					padding: 1.5rem; 
					padding-top: 8rem; 
					max-width: 1100px; 
					margin: 0 auto; 
				}
				.themes-title { font-size: 2rem; margin: 0; color: #111; }
				.themes-subtitle { color: #444; margin-top: 0.4rem; margin-bottom: 1.2rem; }
				.themes-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
				.theme-card { background: white; border: 1px solid #ddd; border-radius: 0.75rem; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.04); display: flex; flex-direction: column; }
				.theme-img { width: 100%; height: 11rem; object-fit: cover; }
				.theme-info { display: flex; align-items: center; justify-content: space-between; padding: 0.8rem; gap: 0.6rem; }
				.theme-name { font-weight: 600; color: #333; }
				.theme-import-button { background: #5271FF; color: white; border: none; padding: 0.5rem 0.8rem; border-radius: 0.5rem; cursor: pointer; font-weight: 600; }
				.theme-import-button[disabled] { opacity: 0.7; cursor: not-allowed; }
				.themes-publish { margin: 0.6rem 0 1rem; }
				.themes-publish .checkbox { display: inline-flex; align-items: center; gap: 0.5rem; color: #333; }
				.themes-instructions { margin-top: 1.5rem; color: #333; background: #fff; border: 1px solid #ddd; border-radius: 0.75rem; padding: 1rem 1.2rem; }
				.themes-instructions-title { font-weight: 600; margin-bottom: 0.5rem; }
				@media (max-width: 950px) {
					.themes-grid { grid-template-columns: 1fr; }
				}
			`}</style>
			{showToast && (
				<Toast
					content={toastContent}
					error={toastIsError}
					onDismiss={() => setShowToast(false)}
					duration={10000}
				/>
			)}
		</div>
	)
}

export default Themes
